import { useEffect } from "react"
import { NotificationMessageData } from "@/lib/push-notification"
import { getChatRoute } from "@/modules/chats/lib/routing-helpers"
import { useAllowTakeOverPatientMutation } from "@/modules/patients/services/useAllowTakeOverPatientMutation"
import { useDenyTakeOverPatientMutation } from "@/modules/patients/services/useDenyTakeOverPatientMutation"

export const PATIENT_FILE_LOCK_REQUESTED = {
  ALLOW: "ALLOW_PATIENT_FILE_LOCK_REQUESTED",
  DENY: "DENY_PATIENT_FILE_LOCK_REQUESTED",
  NOTIFICATION_CLICKED: "NOTIFICATION_CLICKED",
} as const
export type PatientFileLockRequested = (typeof PATIENT_FILE_LOCK_REQUESTED)[keyof typeof PATIENT_FILE_LOCK_REQUESTED]

export const usePatientFileLockRequestNotificationHandler = () => {
  const allowTakeOver = useAllowTakeOverPatientMutation()
  const denyTakeOver = useDenyTakeOverPatientMutation()

  useEffect(() => {
    if (!navigator.serviceWorker) return

    const onMessage = (
      event: MessageEvent<{
        kind: PatientFileLockRequested
        data: NotificationMessageData
      }>,
    ) => {
      const {
        kind,
        data: { appointment, patient },
      } = event.data

      if (!patient) return

      const navigateUrl = appointment?.chatDetails?.chatThreadId
        ? getChatRoute(appointment.chatDetails.chatThreadId)
        : `/patients/${patient.country}/${patient?.id}?appointmentId=${appointment?.id}`

      if (kind === PATIENT_FILE_LOCK_REQUESTED.ALLOW) {
        allowTakeOver({ patientId: patient.id })
      } else if (kind === PATIENT_FILE_LOCK_REQUESTED.DENY) {
        denyTakeOver({ patientId: patient.id })
      } else if (kind === PATIENT_FILE_LOCK_REQUESTED.NOTIFICATION_CLICKED) {
        window.open(navigateUrl, "_blank")
      }
    }

    navigator.serviceWorker.addEventListener("message", onMessage)
    return () => navigator.serviceWorker.removeEventListener("message", onMessage)
  }, [allowTakeOver, denyTakeOver])
}
